<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  watch,
  type CSSProperties,
} from "vue";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import { useEscapeLayer } from "../composables/escape";
import { locale, t } from "../i18n";

/**
 * A day, picked on a month calendar that opens over the page (the same box as the text field and
 * the select). The value is "YYYY-MM-DD"; `min`, `max` and `disabled` limit the choice, and
 * `inline` fits it inside a sentence ("De [24/12] a [26/12]").
 */
const model = defineModel<string>({ default: "" });
const props = withDefaults(
  defineProps<{
    label: string;
    min?: string;
    max?: string;
    /** Days that cannot be picked ("YYYY-MM-DD"). */
    disabledDays?: string[];
    inline?: boolean;
    disabled?: boolean;
  }>(),
  { min: undefined, max: undefined, disabledDays: () => [], inline: false },
);

const open = ref(false),
  root = ref<HTMLElement>(),
  trigger = ref<HTMLButtonElement>(),
  popup = ref<HTMLElement>();
const popupId = useId();
const position = ref<CSSProperties>({
  left: "0px",
  top: "0px",
  visibility: "hidden",
});

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const today = () => {
  const n = new Date();
  return iso(n.getFullYear(), n.getMonth() + 1, n.getDate());
};
const parse = (v: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};
const shown = ref(parse(model.value || today())!);

const intlLocale = computed(() =>
  locale.value === "en" ? "en-US" : locale.value,
);
const display = computed(() => {
  const p = parse(model.value);
  if (!p) return "";
  return new Intl.DateTimeFormat(intlLocale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(p.y, p.m - 1, p.d)));
});
const monthTitle = computed(() =>
  new Intl.DateTimeFormat(intlLocale.value, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(shown.value.y, shown.value.m - 1, 1))),
);
/** Week day initials, Monday first as in Brazil's calendars. */
const weekdays = computed(() =>
  [1, 2, 3, 4, 5, 6, 7].map((i) =>
    new Intl.DateTimeFormat(intlLocale.value, {
      weekday: "narrow",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(2024, 0, i))),
  ),
);
const blocked = computed(() => new Set(props.disabledDays));
const cells = computed(() => {
  const { y, m } = shown.value;
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const out: Array<{ day: number; value: string; off: boolean } | null> = [];
  for (let i = 0; i < lead; i++) out.push(null);
  for (let d = 1; d <= days; d++) {
    const value = iso(y, m, d);
    const off =
      (props.min !== undefined && value < props.min) ||
      (props.max !== undefined && value > props.max) ||
      blocked.value.has(value);
    out.push({ day: d, value, off });
  }
  return out;
});
function shift(months: number) {
  const d = new Date(Date.UTC(shown.value.y, shown.value.m - 1 + months, 1));
  shown.value = { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: 1 };
  void nextTick(place);
}

useEscapeLayer(open, () => close(true));
function close(restoreFocus = false) {
  if (popup.value?.matches(":popover-open")) popup.value.hidePopover();
  open.value = false;
  if (restoreFocus) trigger.value?.focus({ preventScroll: true });
}
function place() {
  if (!open.value || !trigger.value || !popup.value) return;
  const anchor = trigger.value.getBoundingClientRect();
  const gap = 6,
    margin = 8;
  const width = popup.value.offsetWidth;
  const height = popup.value.offsetHeight;
  const below = innerHeight - anchor.bottom - gap - margin;
  const upwards = below < height && anchor.top - gap - margin > below;
  position.value = {
    left:
      Math.max(margin, Math.min(anchor.left, innerWidth - width - margin)) +
      "px",
    top:
      Math.max(
        margin,
        upwards ? anchor.top - gap - height : anchor.bottom + gap,
      ) + "px",
    visibility: "visible",
  };
}
async function toggle() {
  if (open.value) return close(true);
  if (props.disabled) return;
  shown.value = parse(model.value || props.min || today())!;
  position.value = { ...position.value, visibility: "hidden" };
  open.value = true;
  await nextTick();
  popup.value?.showPopover();
  place();
  await nextTick();
  popup.value
    ?.querySelector<HTMLElement>(
      "[aria-selected=true], button.day:not(:disabled)",
    )
    ?.focus({
      preventScroll: true,
    });
}
function pick(value: string) {
  model.value = value;
  close(true);
}
/** Arrows move a day or a week, Page Up/Down a month, as in a native date picker. */
function key(e: KeyboardEvent) {
  const el = document.activeElement as HTMLElement | null;
  const value = el?.dataset.value;
  if (!value) return;
  const step: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -7,
    ArrowDown: 7,
  };
  if (!(e.key in step) && e.key !== "PageUp" && e.key !== "PageDown") return;
  e.preventDefault();
  const p = parse(value)!;
  const d =
    e.key === "PageUp" || e.key === "PageDown"
      ? new Date(Date.UTC(p.y, p.m - 1 + (e.key === "PageUp" ? -1 : 1), p.d))
      : new Date(Date.UTC(p.y, p.m - 1, p.d + step[e.key]!));
  const next = iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  if (
    d.getUTCMonth() + 1 !== shown.value.m ||
    d.getUTCFullYear() !== shown.value.y
  )
    shown.value = { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: 1 };
  void nextTick(() =>
    popup.value
      ?.querySelector<HTMLElement>(`[data-value="${next}"]`)
      ?.focus({ preventScroll: true }),
  );
}
function outside(e: PointerEvent) {
  if (!root.value?.contains(e.target as Node)) close();
}
watch(
  () => props.disabled,
  (d) => {
    if (d) close();
  },
);
onMounted(() => {
  document.addEventListener("pointerdown", outside);
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", outside);
  window.removeEventListener("resize", place);
  window.removeEventListener("scroll", place, true);
});
</script>
<template>
  <div ref="root" class="wa-select date-field" :class="{ inline }">
    <button
      ref="trigger"
      type="button"
      class="select-trigger"
      :class="{ open, empty: !model }"
      :aria-label="label"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-controls="open ? popupId : undefined"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="select-text">
        <span v-if="!inline" class="select-label">{{ label }}</span>
        <span class="select-value">{{ display }}</span>
      </span>
      <Icon name="calendar" :size="20" class="select-arrow" />
    </button>
    <div
      v-if="open"
      :id="popupId"
      ref="popup"
      popover="manual"
      class="date-popup"
      role="dialog"
      :aria-label="label"
      :style="position"
      @keydown="key"
    >
      <div class="date-popup-head">
        <IconButton
          icon="chevronLeft"
          :label="t('date.previous_month')"
          @click="shift(-1)"
        />
        <strong>{{ monthTitle }}</strong>
        <IconButton
          icon="chevron"
          :label="t('date.next_month')"
          @click="shift(1)"
        />
      </div>
      <div class="date-grid" role="grid">
        <span
          v-for="(w, i) in weekdays"
          :key="'w' + i"
          class="date-weekday"
          aria-hidden="true"
          >{{ w }}</span
        >
        <template v-for="(c, i) in cells" :key="i">
          <span v-if="!c" />
          <button
            v-else
            type="button"
            class="day"
            :class="{ today: c.value === today() }"
            :data-value="c.value"
            :aria-selected="c.value === model"
            :disabled="c.off"
            @click="pick(c.value)"
          >
            {{ c.day }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
